import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API_BASE, formatApiError, useAuth } from "./AuthContext";
import { useTeamMembers } from "./AssigneePicker";
import { X, Save, Loader2, GitBranch, StickyNote, Trash2, ExternalLink, UserCheck } from "lucide-react";

const input = "w-full bg-white border border-[var(--border)] rounded-md px-3 py-2 text-[13px] focus:outline-none focus:border-[var(--ink)]";

function toLocalInput(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
}

function JobNotes({ jobId }) {
  const { state } = useAuth();
  const [notes, setNotes] = useState([]);
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => {
    axios.get(`${API_BASE}/jobs/${jobId}/notes`).then((r) => setNotes(r.data)).catch(() => {});
  }, [jobId]);
  useEffect(() => { load(); }, [load]);
  const add = async () => {
    setBusy(true);
    try { await axios.post(`${API_BASE}/jobs/${jobId}/notes`, { body }); setBody(""); load(); }
    catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  const del = async (n) => {
    if (!window.confirm("Delete this note?")) return;
    try { await axios.delete(`${API_BASE}/jobs/${jobId}/notes/${n.note_id}`); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <div className="space-y-2" data-testid="job-notes">
      <div className="overline inline-flex items-center gap-1"><StickyNote size={11} /> Notes · also shown on the client's Activity</div>
      <textarea className={input} rows={2} value={body} onChange={(e) => setBody(e.target.value)} maxLength={5000}
                placeholder="Add a note about this job…" data-testid="job-note-input" />
      <button className="btn-secondary" disabled={busy || !body.trim()} onClick={add} data-testid="job-note-add">
        {busy ? <Loader2 size={12} className="animate-spin" /> : <StickyNote size={12} />} Add note
      </button>
      <ul className="space-y-2">
        {notes.map((n) => (
          <li key={n.note_id} className="card p-3 text-[13px] group" data-testid={`job-note-${n.note_id}`}>
            <div className="whitespace-pre-wrap text-[var(--ink)]">{n.body}</div>
            <div className="flex items-center justify-between mt-1 text-[11px] text-[var(--ink-3)]">
              <span>{n.author_name} · {new Date(n.created_at).toLocaleString()}</span>
              {(n.author_user_id === state?.user?.user_id || ["owner", "manager"].includes(state?.user?.role)) && (
                <button onClick={() => del(n)} className="opacity-0 group-hover:opacity-100 hover:text-[var(--accent)]"
                        data-testid={`job-note-delete-${n.note_id}`}><Trash2 size={12} /></button>
              )}
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

export default function JobDrawer({ jobId, clients, templates, statuses, onClose, onSaved }) {
  const { state } = useAuth();
  const members = useTeamMembers();
  const [job, setJob] = useState(null);
  const [f, setF] = useState(null);
  const [tpl, setTpl] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    axios.get(`${API_BASE}/jobs/${jobId}`).then(({ data }) => {
      setJob(data);
      setF({ title: data.title || "", client_id: data.client_id || "", status: data.status, assignee_id: data.assignee_id || "",
        due_at: toLocalInput(data.due_at), priority: data.priority || "medium", budget_minutes: data.budget_minutes || 0,
        description: data.description || "" });
    }).catch((e) => { toast.error(formatApiError(e)); onClose(); });
  }, [jobId, onClose]);

  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));
  const save = async () => {
    setBusy(true);
    try {
      const body = { ...f, client_id: f.client_id || null, assignee_id: f.assignee_id || null,
        due_at: f.due_at ? new Date(f.due_at).toISOString() : null, budget_minutes: Number(f.budget_minutes) || 0 };
      if (body.status === job.status) delete body.status;
      const { data } = await axios.patch(`${API_BASE}/jobs/${jobId}`, body);
      setJob(data); toast.success("Job saved"); onSaved?.();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(d?.code === "id_not_verified" ? `${d.message} Drag the card to Done on the board to override.` : formatApiError(e));
    } finally { setBusy(false); }
  };
  const attach = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API_BASE}/jobs/${jobId}/attach-workflow`, { template_id: tpl });
      setJob(data); setTpl(""); toast.success(`Workflow "${data.template_name}" attached`); onSaved?.();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  const tplName = templates.find((t) => t.template_id === job?.template_id)?.name;
  return (
    <div className="fixed inset-0 z-[60] bg-black/30 flex justify-end" onClick={onClose} data-testid="job-drawer">
      <div className="w-full max-w-xl h-full bg-[var(--paper)] shadow-2xl overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-[var(--border)] sticky top-0 bg-[var(--paper)] z-10">
          <div className="overline">Edit job</div>
          <button onClick={onClose} data-testid="job-drawer-close"><X size={18} /></button>
        </div>
        {!f ? <div className="p-6"><Loader2 className="animate-spin" /></div> : (
          <div className="p-6 space-y-5">
            <input className={`${input} text-[16px] font-medium`} value={f.title} onChange={set("title")} data-testid="job-edit-title" />
            <div className="grid grid-cols-2 gap-3">
              <label className="text-[11px] text-[var(--ink-3)]">Client
                <select className={input} value={f.client_id} onChange={set("client_id")} data-testid="job-edit-client">
                  <option value="">— No client —</option>
                  {clients.map((c) => <option key={c.client_id} value={c.client_id}>{c.name}</option>)}
                </select>
              </label>
              <label className="text-[11px] text-[var(--ink-3)]">Status
                <select className={input} value={f.status} onChange={set("status")} data-testid="job-edit-status">
                  {statuses.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
                </select>
              </label>
              <label className="text-[11px] text-[var(--ink-3)]">Assigned to
                <div className="flex gap-1">
                  <select className={input} value={f.assignee_id} onChange={set("assignee_id")} data-testid="job-edit-assignee">
                    <option value="">Unassigned</option>
                    {members.map((m) => <option key={m.user_id} value={m.user_id}>{m.name || m.email}</option>)}
                  </select>
                  <button type="button" className="btn-ghost px-2" title="Assign to me" data-testid="job-edit-assign-me"
                          onClick={() => setF((p) => ({ ...p, assignee_id: state?.user?.user_id }))}><UserCheck size={13} /></button>
                </div>
              </label>
              <label className="text-[11px] text-[var(--ink-3)]">Due
                <input type="datetime-local" className={input} value={f.due_at} onChange={set("due_at")} data-testid="job-edit-due" />
              </label>
              <label className="text-[11px] text-[var(--ink-3)]">Priority
                <select className={input} value={f.priority} onChange={set("priority")} data-testid="job-edit-priority">
                  {["low", "medium", "high"].map((p) => <option key={p} value={p}>{p}</option>)}
                </select>
              </label>
              <label className="text-[11px] text-[var(--ink-3)]">Budget (minutes)
                <input type="number" min={0} className={input} value={f.budget_minutes} onChange={set("budget_minutes")} data-testid="job-edit-budget" />
              </label>
            </div>
            <textarea className={input} rows={3} value={f.description} onChange={set("description")} placeholder="Description"
                      data-testid="job-edit-description" />
            <div className="flex items-center gap-3">
              <button className="btn-primary" onClick={save} disabled={busy || !f.title.trim()} data-testid="job-edit-save">
                {busy ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />} Save changes
              </button>
              {job.client_id && (
                <a href={`/clients?client=${job.client_id}&tab=activity`} target="_blank" rel="noreferrer"
                   className="text-[12px] text-[var(--accent)] inline-flex items-center gap-1 hover:underline" data-testid="job-edit-open-client">
                  Open client <ExternalLink size={11} /></a>
              )}
            </div>
            <div className="card p-4 space-y-2" data-testid="job-workflow-section">
              <div className="overline inline-flex items-center gap-1"><GitBranch size={11} /> Workflow</div>
              {job.template_id ? (
                <div className="text-[13px]" data-testid="job-workflow-current">{tplName || "Workflow attached"} · {(job.stages || []).length} stages</div>
              ) : <div className="text-[12px] text-[var(--ink-3)]">No workflow attached yet.</div>}
              <div className="flex gap-2">
                <select className={input} value={tpl} onChange={(e) => setTpl(e.target.value)} data-testid="job-workflow-select">
                  <option value="">{job.template_id ? "Replace with…" : "Choose a workflow…"}</option>
                  {templates.map((t) => <option key={t.template_id} value={t.template_id}>{t.name}</option>)}
                </select>
                <button className="btn-secondary whitespace-nowrap" disabled={!tpl || busy} onClick={attach} data-testid="job-workflow-attach">Attach</button>
              </div>
            </div>
            <JobNotes jobId={jobId} />
          </div>
        )}
      </div>
    </div>
  );
}
